import React from 'react';
import * as Location from './fields/Location';
import * as Dates from './fields/Dates';
import * as Occupancy from './fields/Occupancy';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { SubmitButton } from './components/SubmitButton';
import { FormFieldsWrapper } from './components/FormFieldsWrapper';
import { StaysSearchTerms } from '@apps/stays/types';
import { Box, Divider, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

interface Props {
  onSubmit: (value: StaysSearchTerms) => void;
  initialValue?: Partial<StaysSearchTerms>;
}

export const LocationDatesOccupants = (props: Props) => {
  const today = new Date();
  const threeDaysFromNow = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);
  const isMobileOrTablet = useIsMobileOrTablet();

  const schema = Yup.object({
    location: Location.schema,
    dates: Dates.schema,
    occupancy: Occupancy.schema
  });
  return (
    <Formik
      initialValues={{
        location: {
          displayName: '',
          latitude: 0,
          longitude: 0
        },
        // what I would love to do is use 'satisfies' the type of Dates.initialValue
        dates: {
          startDate: props.initialValue?.startDate
            ? new Date(
                `${props.initialValue.startDate.year}-${props.initialValue.startDate.month.toString().padStart(2, '0')}-${props.initialValue.startDate.day
                  .toString()
                  .padStart(2, '0')}`
              )
            : today,
          endDate: props.initialValue?.endDate
            ? new Date(
                `${props.initialValue.endDate.year}-${props.initialValue.endDate.month.toString().padStart(2, '0')}-${props.initialValue.endDate.day.toString().padStart(2, '0')}`
              )
            : threeDaysFromNow
        },
        // TODO: fix childrenAges type
        occupancy: props.initialValue?.rooms || Occupancy.initialValue
      }}
      onSubmit={value => {
        props.onSubmit({
          destination: value.location.displayName,
          lat: value.location.latitude,
          lng: value.location.longitude,
          startDate: {
            year: value.dates.startDate.getFullYear(),
            month: value.dates.startDate.getMonth() + 1,
            day: value.dates.startDate.getDate()
          },
          endDate: {
            year: value.dates.endDate.getFullYear(),
            month: value.dates.endDate.getMonth() + 1,
            day: value.dates.endDate.getDate()
          },
          rooms: value.occupancy.map(room => ({
            adults: room.adults,
            childrenAges: room.childrenAges
          }))
        });
      }}
      validationSchema={schema}
    >
      <Form name="search-fields" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* TODO: think about how to make this easier to do across different forms, maybe custom provider */}
        {/* Trick to reset the form after async initial values come is */}
        <ResetFormOnNewInitialValue initialValue={props.initialValue} />
        {isMobileOrTablet ? (
          <Flex width={'100%'} flexDirection={'column'}>
            <Location.Fields fieldsId={'location'} />
            <Dates.Fields fieldsId={'dates'} defaultStartDate={today} defaultEndDate={threeDaysFromNow} />
            <Occupancy.Fields fieldsId={'occupancy'} />
            <SubmitButton />
          </Flex>
        ) : (
          <Flex alignItems="center" gap={3}>
            <FormFieldsWrapper>
              <Box gridArea={'location'} height={pxToRem(24)} minWidth={pxToRem(260)}>
                <Location.Fields fieldsId={'location'} />
              </Box>
              <Divider orientation="vertical" height={pxToRem(24)} />
              <Box gridArea={'dates'} height={pxToRem(24)}>
                <Dates.Fields fieldsId={'dates'} defaultStartDate={today} defaultEndDate={threeDaysFromNow} />
              </Box>
              <Divider orientation="vertical" height={pxToRem(24)} />
              <Box gridArea={'occupancy'}>
                <Occupancy.Fields fieldsId={'occupancy'} />
              </Box>
            </FormFieldsWrapper>
            <Box gridArea={'submit'}>
              <SubmitButton />
            </Box>
          </Flex>
        )}
      </Form>
    </Formik>
  );
};

const ResetFormOnNewInitialValue = (props: Pick<Props, 'initialValue'>) => {
  const formik = useFormikContext();
  React.useEffect(() => {
    formik.resetForm({
      values: {
        location: {
          displayName: props.initialValue?.destination,
          latitude: props.initialValue?.lat,
          longitude: props.initialValue?.lng
        },
        dates: {
          startDate: props.initialValue?.startDate
            ? new Date(
                `${props.initialValue.startDate.year}-${props.initialValue.startDate.month.toString().padStart(2, '0')}-${props.initialValue.startDate.day
                  .toString()
                  .padStart(2, '0')}`
              )
            : new Date(),
          endDate: props.initialValue?.endDate
            ? new Date(
                `${props.initialValue.endDate.year}-${props.initialValue.endDate.month.toString().padStart(2, '0')}-${props.initialValue.endDate.day.toString().padStart(2, '0')}`
              )
            : new Date()
        },
        occupancy: props.initialValue?.rooms || Occupancy.initialValue
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValue]);
  return null;
};
